/**
 * Document binding hook — detects whether the current Word document
 * is already linked to a KobSign document (signing, negotiation, or archive).
 *
 * Reads the binding from Custom XML Parts on mount, validates against the
 * backend, and exposes the result so components can render the right UI.
 *
 * Also polls for document switches (user opens a different DOCX) every 3s.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  readDocumentBinding,
  writeDocumentBinding,
  deleteDocumentBinding,
  type DocumentBinding,
} from './useCustomXmlParts'
import { getDocument, type DocumentDetail } from '../services/documentsApi'
import { devLog } from '../services/devLog'

export type BindingState =
  | 'loading'
  | 'unbound'
  | 'bound-active'
  | 'bound-terminal'
  | 'bound-draft'
  | 'no-access'
  | 'error'

export interface UseDocumentBindingResult {
  state: BindingState
  binding: DocumentBinding | null
  document: DocumentDetail | null
  bind: (koblinkId: string, title: string, status: string, actorId?: string) => Promise<void>
  unbind: () => Promise<void>
  refresh: () => void
}

/** Map backend document status to UI binding state */
function mapStatus(status: string): BindingState {
  switch (status) {
    case 'draft':
      return 'bound-draft'
    case 'pending':
    case 'partially_signed':
    case 'negotiating':
      return 'bound-active'
    case 'completed':
    case 'declined':
    case 'recalled':
    case 'expired':
      return 'bound-terminal'
    default:
      return 'bound-active'
  }
}

/** Get the document's file URL from Office.js (for detecting document switches) */
function getDocumentUrl(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      Office.context.document.getFilePropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value.url) {
          resolve(result.value.url)
        } else {
          resolve(null)
        }
      })
    } catch {
      resolve(null)
    }
  })
}

export function useDocumentBinding(actorId?: string): UseDocumentBindingResult {
  const [state, setState] = useState<BindingState>('loading')
  const [binding, setBinding] = useState<DocumentBinding | null>(null)
  const [document, setDocument] = useState<DocumentDetail | null>(null)
  const [refreshCounter, setRefreshCounter] = useState(0)
  const lastDocUrlRef = useRef<string | null>(null)
  const mountedRef = useRef(true)

  const refresh = useCallback(() => {
    setRefreshCounter((c) => c + 1)
  }, [])

  const bind = useCallback(async (koblinkId: string, title: string, status: string, ownerActorId?: string) => {
    const newBinding: DocumentBinding = {
      koblink_id: koblinkId,
      title,
      status,
      bound_at: new Date().toISOString(),
      owner_actor_id: ownerActorId,
    }
    try {
      await writeDocumentBinding(newBinding)
      setBinding(newBinding)
      setState(mapStatus(status))
    } catch (e) {
      devLog.warn('Binding', `Write failed (non-blocking): ${e}`)
    }
  }, [])

  const unbind = useCallback(async () => {
    try {
      await deleteDocumentBinding()
    } catch (e) {
      devLog.warn('Binding', `Delete failed (non-blocking): ${e}`)
    }
    setBinding(null)
    setDocument(null)
    setState('unbound')
  }, [])

  // Check binding on mount, actor change, or refresh
  useEffect(() => {
    let cancelled = false

    async function check() {
      setState('loading')

      try {
        const existing = await readDocumentBinding()
        if (cancelled) return

        if (!existing) {
          setState('unbound')
          setBinding(null)
          setDocument(null)
          return
        }

        setBinding(existing)

        // Validate against backend
        try {
          const doc = await getDocument(existing.koblink_id, actorId)
          if (cancelled) return

          setDocument(doc)
          const newState = mapStatus(doc.status)
          setState(newState)

          // Update cached status if changed
          if (doc.status !== existing.status) {
            writeDocumentBinding({ ...existing, status: doc.status }).catch(() => {})
          }
        } catch (e: unknown) {
          if (cancelled) return

          const status = (e as { status?: number })?.status
          if (status === 404) {
            // Document no longer exists — stale binding
            devLog.info('Binding', 'Stale binding (404) — removing')
            await deleteDocumentBinding().catch(() => {})
            setBinding(null)
            setDocument(null)
            setState('unbound')
          } else if (status === 403) {
            // No access — document belongs to different user/actor
            setDocument(null)
            setState('no-access')
          } else {
            // Network error or other — show binding info without backend data
            devLog.warn('Binding', `Backend validation failed: ${e}`)
            setState(mapStatus(existing.status))
          }
        }
      } catch (e) {
        if (!cancelled) {
          devLog.warn('Binding', `Check failed: ${e}`)
          setState('unbound')
        }
      }
    }

    check()
    return () => { cancelled = true }
  }, [actorId, refreshCounter])

  // Poll for document switches (user opens different DOCX)
  useEffect(() => {
    mountedRef.current = true

    const interval = setInterval(async () => {
      if (!mountedRef.current) return
      try {
        const url = await getDocumentUrl()
        if (!mountedRef.current) return

        if (lastDocUrlRef.current === null) {
          // First check — just record the URL
          lastDocUrlRef.current = url
          return
        }

        if (url !== lastDocUrlRef.current) {
          devLog.info('Binding', 'Document switch detected — refreshing binding')
          lastDocUrlRef.current = url
          refresh()
        }
      } catch {
        // Best-effort
      }
    }, 3000)

    return () => {
      mountedRef.current = false
      clearInterval(interval)
    }
  }, [refresh])

  return { state, binding, document, bind, unbind, refresh }
}
